import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Tag from "../../../fragments/Tag"
import SimpleBoxDisplay from "../../../common/SimpleBoxDisplay"
import NegotiationLiability from "./NegotiationLiability"
import NegotiationMedPay from "./NegotiationMedPay"
import NegotiationUM from "./NegotiationUM"
import TotalsTable from "./TotalsTable"
import PaymentsTable from "./PaymentsTable"
import { caseActions } from "../../../../store/case"
import { acceptNegotiation, caseQuickUpdate, selectLawFirm } from "../../../../lib/fetchApi"
import { notificationActions } from "../../../../store/notifications"
import LawFirmAddress from "../attorney/LawFirmAddress"
import NegotiationEntity from "./NegotiationEntity"
import { returnManagingAttorneyOrTheNextActiveOne } from "../../../../lib/primaryItemScripts"
import { isCaseType } from "../../../../lib/script"
import CollectionRep from "./CollectionRep"
import NegotiationTotals from "./NegotiationTotals"
import DefaultUpdater from "../../../common/DefaultUpdater"
import OutsideLiens from "./outsidelien/OutsideLiens"
import { frontendOptions } from "../../../../lib/systemData"
import { optimisticUpdateCaseNegotiation } from "../../../../lib/negotiationNegStatus"

// old design and layout
// import ItemSimple from "../Shared/ItemSimple"
// import classes from '../Shared/Item.module.css'

// new design and layout
import ItemSimpleV2 from "../Shared/ItemSimpleV2"
import classes from '../Shared/ItemV2.module.css'
import PreviousCareNegotiation from "./PreviousCareNegotiation"
import Button from "../../../common/Button"
import { WorkQueueUpdateContext } from "../../../work_queues/pages/drawer/WorkQueueUpdateContext"
import {
    isNegotiationEscalationEligible,
    isUmNegotiationSplit,
} from "../../../../lib/negotiationEscalationEligibility"
import { useUserTabEditPermissionDisabled } from "../../../../lib/usersPermissions"

export default function NegotiationItem() {
    const dispatch = useDispatch() 
    const activeCase = useSelector(state => state.case.activeCase) 
    const userToken = useSelector(state => state.user.token)    
    const [isEscalating, setIsEscalating] = useState(false)
    const [isAccepting, setIsAccepting] = useState(false)
    const [confirmationAction, setConfirmationAction] = useState(null)
    const workQueueUpdate = useContext(WorkQueueUpdateContext)
    const normalizedActiveTabKey = String(workQueueUpdate?.activeTabKey || '').trim().toLowerCase().replace(/-/g, '_')
    const [priamryAtty, setPriamryAtty] = useState(null)
    const isNegotiationEscalationQueue = normalizedActiveTabKey === 'negotiation_escalation'
    const negotiationEditDisabled = useUserTabEditPermissionDisabled('case_negotiation')
    const editEscalationDisabled = useUserTabEditPermissionDisabled('edit_escalation')
    const canEditNegotiation = !negotiationEditDisabled
    const canShowEscalateButton = !editEscalationDisabled
    const reps = priamryAtty?.all_reps?.reps
    const resolvedNegotiatorRep = reps?.prefered_negotiator_rep || reps?.negotiator_rep
    const resolvedCollectionRep = reps?.prefered_collection_rep || reps?.collection_rep
    const resolvedSalesRep = reps?.prefered_poc_rep || reps?.poc_rep

    const isUmSplitEnabledForAccept = useMemo(() => isUmNegotiationSplit(activeCase), [activeCase])

    const practicesForNegotiation = activeCase?.practices || []

    const hasCounterOffer = useMemo(() => {
        return practicesForNegotiation.some((p) => {
            const hasLiaCounter = p?.lia_neg_counter_offer != null && p?.lia_neg_counter_offer !== ''
            const hasUmCounter = isUmSplitEnabledForAccept
                && p?.um_neg_counter_offer != null
                && p?.um_neg_counter_offer !== ''
            return hasLiaCounter || hasUmCounter
        })
    }, [practicesForNegotiation, isUmSplitEnabledForAccept])

    const acceptedAmountReady = useMemo(() => {
        if (!hasCounterOffer) {
            return true
        }
        return practicesForNegotiation.every((p) => {
            const liaReady = p?.lia_neg_amount != null && p?.lia_neg_amount !== ''
            const umReady = !isUmSplitEnabledForAccept
                || (p?.um_neg_amount != null && p?.um_neg_amount !== '')
            return liaReady && umReady
        })
    }, [hasCounterOffer, practicesForNegotiation, isUmSplitEnabledForAccept])

    const isNegotiationAccepted = useMemo(
        () => Boolean(activeCase?.case_negotiation?.total_accepted_at),
        [activeCase?.case_negotiation?.total_accepted_at]
    )

    const negotiationEscalationEligible = useMemo(
        () => isNegotiationEscalationEligible(activeCase),
        [activeCase]
    )

    const isNegotiationEscalated = useMemo(
        () => Number(activeCase?.case_negotiation?.escalate_negotiation) === 1,
        [activeCase?.case_negotiation?.escalate_negotiation]
    )

    const autoClearEscalationInFlightRef = useRef(false)

    useEffect(() => {
        if (!userToken || !activeCase?.case?.id || !activeCase.case_negotiation) {
            return
        }
        if (activeCase.practices === undefined || activeCase.attorneys === undefined) {
            return
        }
        if (!isNegotiationEscalated || negotiationEscalationEligible || canShowEscalateButton) {
            autoClearEscalationInFlightRef.current = false
            return
        }
        if (autoClearEscalationInFlightRef.current) {
            return
        }
        autoClearEscalationInFlightRef.current = true
        ;(async () => {
            const dispatchObj = {
                folder: 'case_negotiation',
                item: {
                    fields: [{ field: 'escalate_negotiation', value: false }],
                },
            }
            dispatch(caseActions.modifyActiveCaseMultipleObject(dispatchObj))
            const update = await caseQuickUpdate(userToken, activeCase.case.id, {
                type: 'case_negotiation',
                type_id: null,
                fields: { escalate_negotiation: 0 },
            })
            if (update && update.errorMessage) {
                dispatch(notificationActions.setError(update))
            }
            autoClearEscalationInFlightRef.current = false
        })()
    }, [
        userToken,
        activeCase?.case?.id,
        activeCase?.case_negotiation,
        isNegotiationEscalated,
        negotiationEscalationEligible,
        canShowEscalateButton,
        dispatch,
    ])

    useEffect(() => {
        if (activeCase && activeCase.attorneys) {        
            let primary = returnManagingAttorneyOrTheNextActiveOne(activeCase)
            
            if (primary) {
                setPriamryAtty(primary)

                async function getRepsFromlawFirm() {
                    let repsFromlawFirm =  await selectLawFirm(userToken, primary.law_firm_id)
                    if (repsFromlawFirm.id) {
                        // setReps(repsFromlawFirm)
                        //set all reps on the primary attorney
                        dispatch(caseActions.setAllRepsOnItem({
                            item_id: primary.id,
                            folder: 'attorneys',
                            content: repsFromlawFirm
                        }))
                    }
                }        
        
                if (primary && primary.law_firm_id && !primary.all_reps) {
                    getRepsFromlawFirm()
                }
            }            
        }
    }, [activeCase, dispatch, userToken])

    const updateHandler = async (data) => {
        let reqBody = {
            type: data.identifier.type,
            type_id: data.identifier.type_id,
            fields: {
                [data.identifier.field] : data.data.value,
            }
        }

        if (data.identifier.folder == 'case_negotiation') {
            const fieldValue =
                data.identifier.field === 'escalate_negotiation' && data.data.value
                    ? 1
                    : data.data.value;
            optimisticUpdateCaseNegotiation(dispatch, caseActions, [
                { field: data.identifier.field, value: fieldValue },
            ]);
        } else {
            dispatch(
                caseActions.modifyActiveCaseMultiple({
                    folder: data.identifier.folder,
                    item: {
                        itemId: data.identifier.type_id,
                        fields: [
                            {
                                field: data.identifier.field,
                                value: data.data.value,
                            },
                        ],
                    },
                })
            );
        }
        

        let update = await caseQuickUpdate(userToken, activeCase.case.id, reqBody)

        if (update && update.errorMessage) {
            dispatch(notificationActions.setError(update))
        }        
        
    }

    const escalateHandler = async () => {
        if (!canShowEscalateButton || isNegotiationEscalated || isEscalating) {
            return
        }
        setIsEscalating(true)
        const payload = {
            data: { value: 1 },
            identifier: {
                type: 'case_negotiation',
                field: 'escalate_negotiation',
                type_id: null,
                folder: 'case_negotiation',
            },
        }
        await updateHandler(payload)
        setIsEscalating(false)
    }

    const acceptHandler = async () => {
        if (isAccepting || !acceptedAmountReady) {
            return
        }
        setIsAccepting(true)

        optimisticUpdateCaseNegotiation(dispatch, caseActions, [
            { field: 'escalate_negotiation', value: 0 },
            { field: 'total_accepted_at', value: new Date().toISOString() },
        ]);

        // Frontend-only copy: offered → negotiated only where neg amount is still null (matches API).
        const practices = activeCase?.practices || []
        for (const practiceItem of practices) {
            const fields = []
            if (practiceItem?.lia_neg_amount == null) {
                fields.push({
                    field: 'lia_neg_amount',
                    value: practiceItem?.lia_neg_offered_amount ?? null,
                })
            }
            if (isUmSplitEnabledForAccept && practiceItem?.um_neg_amount == null) {
                fields.push({
                    field: 'um_neg_amount',
                    value: practiceItem?.um_neg_offered_amount ?? null,
                })
            }
            if (fields.length) {
                dispatch(
                    caseActions.modifyActiveCaseMultiple({
                        folder: 'practices',
                        item: {
                            itemId: practiceItem.id,
                            fields,
                        },
                    })
                )
            }
        }

        const response = await acceptNegotiation(userToken, activeCase.case.id, null, dispatch)
        if (response && response.errorMessage) {
            dispatch(notificationActions.setError(response))
        }

        setIsAccepting(false)
    }

    const openEscalateConfirmation = () => {
        if (!canShowEscalateButton || isNegotiationEscalated || isEscalating) {
            return
        }
        setConfirmationAction('escalate')
    }

    const openAcceptConfirmation = () => {
        if (isAccepting || !acceptedAmountReady) {
            return
        }
        setConfirmationAction('accept')
    }

    const renderNegotiationActionButtons = () => {
        if (!canEditNegotiation) {
            return null
        }

        const acceptBtnStyle = isNegotiationAccepted ? 'btn_accept_bright' : 'btn_accept_dark'
        const showEscalate = canShowEscalateButton
        const escalateBtnStyle = isNegotiationEscalated ? 'btn_escalated' : 'btn_escalate_idle'
        const escalateDisabled = isNegotiationEscalated || isEscalating

        return (
            <div className="flex-container margin-top-8" style={{ justifyContent: 'center', marginTop: 16, gap: 8 }}>
                <Button
                    click={openAcceptConfirmation}
                    btnStyle={acceptBtnStyle}
                    isLoading={isAccepting}
                    isDisabled={!acceptedAmountReady}
                >
                    Accept
                </Button>
                {showEscalate ? (
                    <Button
                        click={openEscalateConfirmation}
                        btnStyle={escalateBtnStyle}
                        isLoading={isEscalating}
                        isDisabled={escalateDisabled}
                    >
                        Escalate
                    </Button>
                ) : null}
            </div>
        )
    }

    const closeConfirmationModal = () => {
        setConfirmationAction(null)
    }

    const confirmActionHandler = async () => {
        const actionType = confirmationAction
        setConfirmationAction(null)

        if (actionType === 'accept') {
            await acceptHandler()
        }
        if (actionType === 'escalate') {
            await escalateHandler()
        }
    }

    const confirmationMessage = confirmationAction === 'accept'
        ? 'Are you sure you want to accept this negotiation?'
        : 'Are you sure you want to escalate this negotiation?'

    const confirmationModal = confirmationAction ? (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'var(--background)', borderRadius: 8, padding: 16, minWidth: 320, boxShadow: '0 6px 20px rgba(0,0,0,0.25)' }}>
                <h3 style={{ margin: 0, marginBottom: 8 }}>Confirmation Required</h3>
                <p style={{ margin: 0, marginBottom: 16 }}>{confirmationMessage}</p>
                <div className="flex-container" style={{ justifyContent: 'flex-end', gap: 8 }}>
                    <Button click={closeConfirmationModal} btnStyle={'btn_white'}>Cancel</Button>
                    <Button click={confirmActionHandler} btnStyle={confirmationAction === 'escalate' ? 'btn_danger_variation' : undefined}>Confirm</Button>
                </div>
            </div>
        </div>
    ) : null

    if (isNegotiationEscalationQueue) {
        return <ItemSimpleV2>
            <div className={classes.item_inner}>
                <div className={`${classes.panel} ${classes.panel_padding}`}>
                    <div style={{ width: '100%' }}>
                        <TotalsTable practices={activeCase.practices} />
                        {renderNegotiationActionButtons()}
                    </div>
                </div>
                {confirmationModal}
            </div>
        </ItemSimpleV2>
    }

    return <ItemSimpleV2>
        <div className={classes.item_inner}>
            {priamryAtty && 
                <div className={`${classes.item_header} ${classes.row_item_full}`}>
                    
                    <div>
                        <div className={classes.name}>
                            <Tag type={'note'}>{priamryAtty.law_firm.code}</Tag>
                            <h2>{priamryAtty.law_firm.name}</h2>
                        </div>
                        {(priamryAtty.location?.contact_information || priamryAtty.law_firm.contact_information) && <div className={classes.location}>
                            <i className='ti ti-building-skyscraper'></i>
                            <LawFirmAddress lawFirmData={priamryAtty}/>                        
                        </div>}
                    </div>
                    <div className="flex-container">
                        {priamryAtty.law_firm && priamryAtty.law_firm.pickup_checks_occurrence && <div className={classes.header_controls}>
                            <SimpleBoxDisplay label={'Pick-up checks occurence'} value={priamryAtty.law_firm.pickup_checks_occurrence}/>
                        </div>}
                        <DefaultUpdater type={'select'} value={activeCase?.case_negotiation?.negotiation_type} options={frontendOptions()?.negotiationsTypeOptions} label={'Negotiation Type'} identifier={{type: 'case_negotiation', field: 'negotiation_type', type_id: null, folder: 'case_negotiation'}} extraOptions={{hasRemove: true}}/>

                        <DefaultUpdater type={'input'} value={activeCase.additional?.people_involved} label={'People involved in the accident'} identifier={{type: 'case_additional', permissionType: 'case_negotiation', field: 'people_involved', type_id: null, folder: 'additional'}}/>
                        <DefaultUpdater type={'input'} value={activeCase.case_police_report?.number_of_vehicles} label={'Number of Vehicles'} identifier={{type: 'case_police_report', permissionType: 'case_negotiation', field: 'number_of_vehicles', type_id: null, folder: 'case_police_report'}} extraOptions={{type: 'number'}}/>
                    </div>
                    
                    
                </div>
            }
            
            <div className={classes.row}>
                {priamryAtty && <NegotiationEntity primaryAttorney={priamryAtty}/>}
                {reps && <>
                    {resolvedNegotiatorRep && <CollectionRep label={'Negotiator'} value={resolvedNegotiatorRep?.name} contact_info={resolvedNegotiatorRep} type={'Representative'}/>}
                

                    {resolvedCollectionRep && <CollectionRep label={'Collections Rep'} value={resolvedCollectionRep?.name} contact_info={resolvedCollectionRep} type={'Representative'}/>}
            {/* from Marketing Rep to POC */}
                    {resolvedSalesRep && <CollectionRep label={'POC'} value={resolvedSalesRep?.name} contact_info={resolvedSalesRep} type={'Representative'}/>}
            
                </>}
            </div>

            <div className={classes.panel}>
                {(isCaseType(activeCase) !== 'WC' && isCaseType(activeCase) !== 'WELL') && <NegotiationLiability updateHandler={updateHandler}/>}
                {(isCaseType(activeCase) !== 'WC' && isCaseType(activeCase) !== 'WELL') && <NegotiationMedPay updateHandler={updateHandler}/>}
                {(isCaseType(activeCase) !== 'WC' && isCaseType(activeCase) !== 'WELL') && <NegotiationUM updateHandler={updateHandler}/>}
            </div>

            <div className={classes.panel}>
                <NegotiationTotals updateHandler={updateHandler}/>
            </div>
            <div className={`${classes.panel} ${classes.panel_padding}`}>
                <div style={{ width: '100%' }}>
                    <TotalsTable practices={activeCase.practices} />
                    {renderNegotiationActionButtons()}
                </div>
            </div>            
  
            <div className={`${classes.panel} ${classes.panel_padding}`}>
                <PaymentsTable />
            </div>

            <div className={`${classes.panel} ${classes.panel_padding} ${classes.panel_columns}`}>
                {/* <PreviousCareSection apiCase={'case_additional'} permissionType={'case_negotiation'}/>  */}
                <PreviousCareNegotiation apiCase={'case_additional'} permissionType={'case_negotiation'}/>
                <OutsideLiens />
            </div>
            {confirmationModal}
        </div>   
    </ItemSimpleV2>


    // OLD LAYOUT
    // return <ItemSimple>
    //     <div className={classes.item_inner}>
    //         <div className={classes.item_left}>
    //             {priamryAtty && <div className={`${classes.row}`}>
    //                 <div className={`${classes.item_header} ${classes.row_item_full}`}>
                        
    //                     <div>
    //                         <div className={classes.name}>
    //                             <Tag type={'note'}>{priamryAtty.law_firm.code}</Tag>
    //                             <h2>{priamryAtty.law_firm.name}</h2>
    //                         </div>
    //                         {priamryAtty.law_firm.contact_information && <div className={classes.location}>
    //                             <i className='ti ti-building-skyscraper'></i>
    //                             <LawFirmAddress lawFirmData={priamryAtty}/>                        
    //                         </div>}
    //                     </div>
    //                     {priamryAtty.law_firm && priamryAtty.law_firm.pickup_checks_occurrence && <div className={classes.header_controls}>
    //                         <SimpleBoxDisplay label={'Pick-up checks occurence'} value={priamryAtty.law_firm.pickup_checks_occurrence}/>
    //                     </div>}
                        
    //                 </div>
    //             </div>}
    //             <div className={classes.separator}></div>
    //             <div className={classes.row}>
    //                 <NegotiationEntity primaryAttorney={priamryAtty}/>
    //             </div>

    //             <div className={classes.panel}>
    //                 {(isCaseType(activeCase) !== 'WC' && isCaseType(activeCase) !== 'WELL') && <NegotiationLiability updateHandler={updateHandler}/>}
    //                 {(isCaseType(activeCase) !== 'WC' && isCaseType(activeCase) !== 'WELL') && <NegotiationMedPay updateHandler={updateHandler}/>}
    //                 {(isCaseType(activeCase) !== 'WC' && isCaseType(activeCase) !== 'WELL') && <NegotiationUM updateHandler={updateHandler}/>}
    //             </div>
    //         </div>
    //         <div className={classes.item_right}>
    //             <NegotiationSidebar primary={priamryAtty} updateHandler={updateHandler}/>
    //         </div>
    //     </div>        
    //     <div className={classes.item_inner}>
    //         <TotalsTable />
    //     </div>
    //     <div className={classes.item_inner}>
    //         <PaymentsTable />
    //     </div>
    // </ItemSimple>
}